import { createSelector } from '@reduxjs/toolkit';
import { DefaultRootState } from 'react-redux';
import {
  roundedDurationSelector,
  holterStripInfoSelector,
} from '../../ecg/selectors/ecg.selectors';
import { OnsetWithWave } from '../../ecg/types/qrs-onset.type';
import { showParentHRPlotSelector } from '../../layout/selectors/layout.selectors';
import { getMovingWindow } from '../../navigation/utils/timestamp.utils';
import { THREE_LEAD_DURATION } from '../../plot/components/ThreeLeadPlot';
import {
  SCATTER_DOTS_SIZE,
  STRIP_SCATTER_DOTS_SIZE,
} from '../../plot/utils/graph.utils';
import {
  isToolActiveSelector,
  toolSelector,
} from '../../tool/selectors/tool.selectors';
import { Tool } from '../../tool/utils/tool.utils';
import { qrsWaveListSelector } from '../../wave/selectors/wave.selectors';
import {
  buildScatterPlotData,
  calculateHolterDuration,
} from '../utils/scatter.utils';

export const scatterSelector = ({ scatter }: DefaultRootState) => scatter;

export const parentOnsetSelector = createSelector(
  scatterSelector,
  ({ onsets }) => onsets,
);

export const onsetsLoadedSelector = createSelector(
  scatterSelector,
  ({ loaded }) => loaded,
);

export const hasParentOnsetsSelector = createSelector(
  parentOnsetSelector,
  (onsets) => onsets.length > 0,
);

export const useParentOnsetsSelector = createSelector(
  hasParentOnsetsSelector,
  showParentHRPlotSelector,
  (hasParentOnsets, showParentHRPlot) => hasParentOnsets && showParentHRPlot,
);

export const onsetsSelector = createSelector(
  useParentOnsetsSelector,
  parentOnsetSelector,
  qrsWaveListSelector,
  (useParentOnsets, parentOnsets, qrsWaves) => {
    let onsets: OnsetWithWave[];
    if (useParentOnsets) {
      onsets = parentOnsets.map((onset) => ({ time: onset.time }));
      return onsets;
    }
    onsets = qrsWaves.map((wave) => ({ time: wave.onset, wave }));
    return onsets;
  },
);

export const scatterPlotDurationSelector = createSelector(
  useParentOnsetsSelector,
  roundedDurationSelector,
  parentOnsetSelector,
  (useParentOnsets, ecgDuration, onsets) =>
    useParentOnsets ? calculateHolterDuration(onsets) : ecgDuration,
);

/**
 * The navigator must appear [1] only for strips above 10 secs (stripDuration > 10_000), [2] in the case where
 * it is not in parent holter view and [3] when the episode measurement is not editing on hr plot.
 * When a strip has no holter, the condition for this is: !hasParentOnsets
 * When there is a parent holter but it is on strip view the condition is: !showParentHRPlot
 * Condition when the ruler is not editing on hr plot: !isEpisodeMeasurementEditing
 */
export const allowNavigatorSelector = createSelector(
  hasParentOnsetsSelector,
  showParentHRPlotSelector,
  scatterPlotDurationSelector,
  isToolActiveSelector(Tool.episodeMeasurement),
  (
    hasParentOnsets,
    showParentHRPlot,
    stripDuration,
    isEpisodeMeasurementActive,
  ) =>
    stripDuration > THREE_LEAD_DURATION &&
    (!hasParentOnsets || !showParentHRPlot) &&
    !isEpisodeMeasurementActive,
);

export const stripStartSelector = createSelector(
  holterStripInfoSelector,
  useParentOnsetsSelector,
  roundedDurationSelector,
  (stripInfo, useParentOnsets, ecgDuration) =>
    stripInfo != null && useParentOnsets ? stripInfo.start : ecgDuration / 2,
);

export const stripOffsetSelector = createSelector(
  holterStripInfoSelector,
  useParentOnsetsSelector,
  scatterPlotDurationSelector,
  (stripInfo, useParentOnsets, scatterPlotDuration) => {
    if (stripInfo == null || !useParentOnsets) {
      return 0;
    }
    const { start, parentDuration } = stripInfo;
    return getMovingWindow(start, scatterPlotDuration, parentDuration).start;
  },
);

export const scatterPointsSelector = createSelector(
  stripStartSelector,
  scatterPlotDurationSelector,
  onsetsSelector,
  (stripStart, duration, onsets) => {
    return buildScatterPlotData(stripStart, duration, onsets);
  },
);

export const scatterDotsRadiusSelector = createSelector(
  useParentOnsetsSelector,
  (showParentHRPlot) =>
    showParentHRPlot ? SCATTER_DOTS_SIZE : STRIP_SCATTER_DOTS_SIZE,
);

export const cursorSelector = createSelector(
  useParentOnsetsSelector,
  toolSelector,
  (hasParentOnsets, { activeTool }) =>
    activeTool === Tool.episodeMeasurement && !hasParentOnsets
      ? 'crosshair'
      : (activeTool === Tool.ruler || activeTool === Tool.rulerAnchored) &&
        !hasParentOnsets
      ? 'not-allowed'
      : undefined,
);
